// Odometer entry check — the ONE source of truth for "does this reading make sense".
//
// CommonJS on purpose (same reason as leaveRules.js): webpack imports it into the
// React portal, where the driver types the number, AND netlify/functions/shift-odo-sb.js
// requires it directly, where the number is recorded. The two can never disagree.
//
// History (19-20 Aug 2026): TORRESJ keyed the tenths digit off the dash — a reading
// of 8375.1 entered as 83751 — on every shift from 22 June. Each day looked
// internally consistent (start 83423, end 83586, a tidy 163 km) so nothing caught
// it: his real 21 km round was logged as 163 km for 40 shifts, and FJS88H's
// odometer read 83,751 on a van that had actually done 8,375. The READ side was
// already typo-robust — getLatestOdometer takes a median of the last 7 readings —
// but a median cannot help when every reading is wrong.
//
// Nothing here blocks a save, and it must stay that way. An odometer can
// legitimately jump (a replaced cluster, a van that sat unread for a month), and
// refusing a driver's shift start at 5am over a suspicious number is a worse
// failure than a bad row in a log. The portal asks "did you mean X?" and takes yes
// for an answer.
//
// Both thresholds below are measured against real history (2,105 shifts and 4,097
// odometer entries since 1 May 2026), not guessed. Replaying the checks over that
// history is the test: see the numbers quoted on each constant.

// Ceiling on ONE shift's distance (closing reading − opening reading).
//
// This is the check that catches a driver who has been wrong from their very first
// entry — which no trail comparison can, because their opening reading set the
// trail. TORRESJ's first entry was already tenfold, so every later reading agreed
// with a trail that was itself wrong and nothing looked out of place. His DAY
// distance did: 163 km against a fleet that runs 12-60.
//
// 120 km: the fleet median day is 20 km, p95 is 45, p99 is 66, and the largest
// ordinary day on record is 125. At 120 this catches 34 of TORRESJ's 35 shifts
// (the miss is the one already corrected to 21 km) and flags 6 of 2,105 other
// shifts — 0.29%, every one of them a genuine mis-key or a day worth a look.
// Dropping to 100 catches nothing more of his and adds three false alarms.
const DAY_KM_MAX = 120;

// Below this there's no meaningful trail to compare a reading against (new van,
// first entry, or a rego whose history was just rebased).
const MIN_TRAIL_KM = 100;

// Deliberately NOT checked here: readings that go backwards, or leap further ahead
// than the days elapsed allow. Both were built, then measured and removed — they
// fired on 4.3% of all entries (1 in 23), and spot-checking says the great majority
// are not typing errors at all but a stale driver→van mapping: resolveRego falls
// back to the driver's `fleet` row, so after a van swap one rego's trail collects
// readings from two different vehicles (CWT06E alternating 262,280 and 32,152 is a
// van change, not a typo). Warning a driver on 1 in 23 entries for something they
// did not do teaches them to dismiss the warning that matters. Fix the rego
// mapping and they can be reconsidered.

const num = (v) => {
  const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, ''));
  return isFinite(n) ? n : 0;
};

const fmt = (n) => Math.round(n).toLocaleString('en-AU');

// A single reading, against what this van last read.
//
// reading   — what the driver typed (km)
// lastKnown — the van's established reading, from getLatestOdometer (already
//             median-filtered, so one earlier typo can't poison this)
// lastDate  — ISO date of that reading, for the message
// rego      — for the message; optional
//
// → { ok: true } | { ok: false, code: 'TENFOLD', message, suggestion }
//
// An extra digit is the one slip with an unambiguous fix, so it's the only reading-
// level check: 11 of 4,097 historical entries (0.27%) land in the 8-12x band, and
// all eleven are real (433,359 against a 43,337 trail; 2,614,388 against 261,439).
function checkOdometer({ reading, lastKnown, lastDate, date, rego } = {}) {
  const r = num(reading);
  const last = num(lastKnown);
  const van = (rego || 'This van').toString().toUpperCase();
  if (r <= 0 || last < MIN_TRAIL_KM) return { ok: true };

  const ratio = r / last;
  if (ratio < 8 || ratio > 12) return { ok: true };

  const seen = lastDate
    ? `${van} last read ${fmt(last)} km on ${String(lastDate).slice(0, 10)}`
    : `${van} last read ${fmt(last)} km`;
  const suggestion = Math.round(r / 10);
  return {
    ok: false,
    code: 'TENFOLD',
    suggestion,
    message: `${seen}. ${fmt(r)} is about ten times that — check you haven't included the decimal at the end. Did you mean ${fmt(suggestion)} km?`,
  };
}

// The distance claimed for one shift. Checked when the driver finishes, while the
// van is still in front of them.
//
// → { ok: true } | { ok: false, code, message, km, suggestion }
//   codes: DAY_TOO_FAR (further than a round goes), DAY_BACKWARDS (end below start)
function checkDayDistance({ odoStart, odoEnd } = {}) {
  const a = num(odoStart);
  const b = num(odoEnd);
  if (a <= 0 || b <= 0) return { ok: true };
  const km = b - a;

  if (km < 0) {
    return {
      ok: false,
      code: 'DAY_BACKWARDS',
      km,
      suggestion: 0,
      message: `Your closing reading (${fmt(b)} km) is lower than your opening one (${fmt(a)} km). Please check both.`,
    };
  }

  if (km > DAY_KM_MAX) {
    // Deliberately NO suggested correction. When a day is tenfold it's because BOTH
    // readings carry the extra digit, so there is no single number to hand back —
    // a tenth of the distance is a distance, and writing it into the closing
    // reading would leave the opening one wrong and the closing one nonsense. The
    // driver has the dash in front of them; ask them to read both again.
    return {
      ok: false,
      code: 'DAY_TOO_FAR',
      km,
      suggestion: 0,
      message: `That's ${fmt(km)} km for the day (${fmt(a)} → ${fmt(b)}). A round is normally 12-60 km, so please check both readings against the dash — the usual cause is including the decimal on the end of the odometer.`,
    };
  }

  return { ok: true };
}

module.exports = {
  DAY_KM_MAX,
  MIN_TRAIL_KM,
  checkOdometer,
  checkDayDistance,
};
